import React, { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { BaseFilterProps } from "../../lib";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

const CLIENT_OPTION_VALUE = "client";

interface Props extends BaseFilterProps {
    /** Adds a "Client" option matching tasks not created by an org member. */
    hasClientOption?: boolean;
}

export const TaskCreatedByFilter: React.FC<Props> = ({
    filters,
    onChange,
    hasClientOption = false,
}) => {
    const { data: members } = useCurrentOrganizationMembers();

    const options = useMemo((): FilterMultiselectOption[] => {
        const memberOptions = (members ?? []).map((member) => ({
            value: member.user.id,
            label: member.user.name,
            searchableLabel: `${member.user.name} ${member.user.email ?? ""}`,
        }));
        return hasClientOption
            ? [
                  { value: CLIENT_OPTION_VALUE, label: "Client" },
                  ...memberOptions,
              ]
            : memberOptions;
    }, [members, hasClientOption]);

    const value = useMemo((): FilterMultiselectValue[] => {
        const ids = filters.taskCreatedByIds ?? [];
        return hasClientOption && filters.isTaskCreatedByClient
            ? [CLIENT_OPTION_VALUE, ...ids]
            : ids;
    }, [
        filters.taskCreatedByIds,
        filters.isTaskCreatedByClient,
        hasClientOption,
    ]);

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={(val) => {
                    const ids = val.filter(
                        (v): v is number => typeof v === "number",
                    );
                    onChange({
                        taskCreatedByIds: ids.length > 0 ? ids : undefined,
                        isTaskCreatedByClient: val.includes(CLIENT_OPTION_VALUE)
                            ? true
                            : undefined,
                    });
                }}
                name="taskCreatedByIds"
                onBlur={noop}
                searchable
            />
        </div>
    );
};
